import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Video, Upload, Plus, Play, Trash2, Tag, FileText,
  ThumbsUp, ThumbsDown, MessageCircle, Loader2, X, Eye, Lock, ToggleLeft, ToggleRight
} from 'lucide-react';

export default function PracticeVideosPage() {
  const { childId } = useParams();
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [childName, setChildName] = useState('');
  const [instructorNote, setInstructorNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [practiceStatus, setPracticeStatus] = useState(null);
  const [togglingPractice, setTogglingPractice] = useState(false);
  const fileRef = useRef(null);

  // Upload form
  const [file, setFile] = useState(null);
  const [skillLabels, setSkillLabels] = useState('');
  const [description, setDescription] = useState('');
  const [howCompleted, setHowCompleted] = useState('');
  const [whatWorked, setWhatWorked] = useState('');
  const [whatDidntWork, setWhatDidntWork] = useState('');

  const isParent = user?.role === 'parent';
  const isInstructor = user?.role === 'instructor';

  const fetchChild = useCallback(async () => {
    try {
      const res = await api.get(`/children/${childId}`);
      setChildName(res.data.name || '');
    } catch (err) { process.env.NODE_ENV === 'development' && console.error('Failed to fetch child:', err); }
  }, [childId]);

  const fetchPracticeStatus = useCallback(async () => {
    try {
      const res = await api.get(`/practice-videos/status/${childId}`);
      setPracticeStatus(res.data);
    } catch (err) { process.env.NODE_ENV === 'development' && console.error('Failed to fetch practice status:', err); }
  }, [childId]);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await api.get(`/practice-videos?child_id=${childId}`);
      setVideos(res.data);
    } catch (err) { process.env.NODE_ENV === 'development' && console.error('Failed to fetch practice videos:', err); } finally { setLoading(false); }
  }, [childId]);

  useEffect(() => {
    fetchVideos();
    fetchChild();
    fetchPracticeStatus();
  }, [fetchVideos, fetchChild, fetchPracticeStatus]);

  const togglePractice = async () => {
    setTogglingPractice(true);
    try {
      const res = await api.put(`/practice-videos/toggle/${childId}`);
      setPracticeStatus(prev => ({ ...prev, practice_enabled: res.data.practice_enabled }));
      toast.success(res.data.practice_enabled ? 'Practice Videos enabled!' : 'Practice Videos paused');
    } catch { toast.error('Failed to toggle'); }
    finally { setTogglingPractice(false); }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a video file'); return; }
    if (!skillLabels.trim()) { toast.error('Please add at least one skill label'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('child_id', childId);
      formData.append('skill_labels', skillLabels);
      formData.append('description', description);
      formData.append('how_completed', howCompleted);
      formData.append('what_worked', whatWorked);
      formData.append('what_didnt_work', whatDidntWork);

      await api.post('/practice-videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Practice video uploaded!');
      setShowUpload(false);
      resetForm();
      fetchVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload video');
    } finally { setUploading(false); }
  };

  const resetForm = () => {
    setFile(null);
    setSkillLabels('');
    setDescription('');
    setHowCompleted('');
    setWhatWorked('');
    setWhatDidntWork('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this practice video?')) return;
    try {
      await api.delete(`/practice-videos/${videoId}`);
      toast.success('Video deleted');
      setVideos(prev => prev.filter(v => v.video_id !== videoId));
      if (showDetail?.video_id === videoId) setShowDetail(null);
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveInstructorNote = async () => {
    if (!showDetail) return;
    setSavingNote(true);
    try {
      await api.put(`/practice-videos/${showDetail.video_id}`, { instructor_notes: instructorNote });
      toast.success('Note saved');
      setShowDetail(prev => ({ ...prev, instructor_notes: instructorNote }));
      setVideos(prev => prev.map(v => v.video_id === showDetail.video_id ? { ...v, instructor_notes: instructorNote } : v));
    } catch { toast.error('Failed to save note'); }
    finally { setSavingNote(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-12">
      <img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="practice-videos-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
            Practice at Home
          </h1>
          <p className="text-slate-500 mt-1">
            {childName && `${childName} — `}
            {isParent ? 'Share videos of skills you practise with your child in the water' : 'View practice videos uploaded by parents'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Instructor toggle */}
          {isInstructor && practiceStatus && (
            <Button
              variant="outline"
              onClick={togglePractice}
              disabled={togglingPractice}
              className={`rounded-full gap-2 font-bold text-sm ${practiceStatus.practice_enabled ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}
              data-testid="toggle-practice-btn"
            >
              {practiceStatus.practice_enabled
                ? <><ToggleRight className="w-4 h-4" /> Enabled</>
                : <><ToggleLeft className="w-4 h-4" /> Disabled</>
              }
            </Button>
          )}
          {isParent && practiceStatus?.practice_enabled && (
            <Button
              data-testid="upload-practice-video-btn"
              onClick={() => setShowUpload(true)}
              className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
            >
              <Plus className="w-4 h-4" /> Upload Video
            </Button>
          )}
        </div>
      </div>

      {/* Practice status info for instructor */}
      {isInstructor && practiceStatus && (
        <div className={`rounded-2xl border p-4 mb-6 ${practiceStatus.practice_enabled ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`} data-testid="practice-status-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-700">
                {practiceStatus.practice_enabled ? 'Practice Videos are ON' : 'Practice Videos are OFF'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {practiceStatus.program_complete
                  ? `Program complete (${practiceStatus.session_count} sessions). Practice auto-enabled.`
                  : `${practiceStatus.session_count} of 32 sessions completed. ${practiceStatus.practice_enabled ? 'You enabled this early for homework.' : 'Toggle on to assign homework before program completion.'}`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Locked state for parent when practice is not enabled */}
      {isParent && practiceStatus && !practiceStatus.practice_enabled && (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200 mb-6" data-testid="practice-locked">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Lock className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>Practice Videos Locked</p>
            <p className="text-slate-400 mt-2 text-sm text-center max-w-md">
              {practiceStatus.program_complete
                ? 'Your instructor has paused Practice Videos. They will re-enable when ready.'
                : `This feature unlocks after completing the 32-session program (${practiceStatus.session_count} of 32 done). Your instructor can also enable it early for homework assignments.`
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info Banner */}
      {isParent && practiceStatus?.practice_enabled && videos.length === 0 && (
        <Card className="rounded-2xl border-[#22D3EE]/20 bg-[#ECFEFF]/40 mb-6" data-testid="practice-info-banner">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Video className="w-6 h-6 text-[#22D3EE] shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-slate-700 text-sm">Share your practice sessions!</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  We encourage parents to practise aquatic skills with their child in their own time.
                  Upload a video of what you worked on, label the skills, and describe what worked and
                  what didn't. Your instructor will review and may provide feedback!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Videos List - show when enabled or for instructor */}
      {(isInstructor || practiceStatus?.practice_enabled) && (videos.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200" data-testid="no-practice-videos">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Video className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Practice Videos Yet</p>
            <p className="text-slate-400 mt-2 text-sm text-center max-w-sm">
              {isParent
                ? 'Upload videos of skills you practise with your child in aquatic environments'
                : 'Parents have not uploaded any practice videos yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="practice-videos-grid">
          {videos.map(video => (
            <Card
              key={video.video_id}
              className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
              onClick={() => { setShowDetail(video); setInstructorNote(video.instructor_notes || ''); }}
              data-testid={`practice-video-${video.video_id}`}
            >
              <div className="relative bg-slate-900 aspect-video flex items-center justify-center">
                <video
                  src={`${BACKEND_URL}/api/uploads/${video.filename}`}
                  className="w-full h-full object-cover"
                  preload="metadata"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <Play className="w-12 h-12 text-white/80" />
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {video.skill_labels?.map((label, i) => (
                    <Badge key={`pvb-${i}`} className="rounded-full bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]/30 text-xs">
                      {label}
                    </Badge>
                  ))}
                </div>
                {video.description && (
                  <p className="text-sm text-slate-600 line-clamp-2">{video.description}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-slate-400">
                    {new Date(video.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {video.instructor_notes && (
                      <Badge className="rounded-full bg-purple-100 text-purple-700 text-[10px] border-purple-200">
                        <MessageCircle className="w-3 h-3 mr-0.5" /> Feedback
                      </Badge>
                    )}
                    {video.what_worked && <ThumbsUp className="w-3.5 h-3.5 text-green-500" />}
                    {video.what_didnt_work && <ThumbsDown className="w-3.5 h-3.5 text-red-400" />}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ))}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#22D3EE]" /> Upload Practice Video
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Video File */}
            <div>
              <label className="text-sm font-semibold text-slate-600">Video File <span className="text-red-400">*</span></label>
              <Input
                ref={fileRef}
                type="file"
                accept="video/*"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="rounded-xl mt-1"
                data-testid="practice-video-file-input"
              />
              {file && <p className="text-xs text-slate-400 mt-1">{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</p>}
            </div>

            {/* Skill Labels */}
            <div>
              <label className="text-sm font-semibold text-slate-600">Skills Practised <span className="text-red-400">*</span></label>
              <Input
                value={skillLabels}
                onChange={e => setSkillLabels(e.target.value)}
                placeholder="e.g. Floating, Kicking, Blowing bubbles"
                className="rounded-xl mt-1"
                data-testid="practice-video-skills"
              />
              <p className="text-xs text-slate-400 mt-1">Separate multiple skills with commas</p>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-semibold text-slate-600">What did you do?</label>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe the activity you did with your child..."
                className="rounded-xl mt-1"
                rows={2}
                data-testid="practice-video-description"
              />
            </div>

            {/* How completed */}
            <div>
              <label className="text-sm font-semibold text-slate-600">How did you get your child to complete the skill?</label>
              <Textarea
                value={howCompleted}
                onChange={e => setHowCompleted(e.target.value)}
                placeholder="What strategies or motivations helped them engage..."
                className="rounded-xl mt-1"
                rows={2}
                data-testid="practice-video-how"
              />
            </div>

            {/* What worked */}
            <div>
              <label className="text-sm font-semibold text-slate-600 flex items-center gap-1">
                <ThumbsUp className="w-3.5 h-3.5 text-green-500" /> What worked well?
              </label>
              <Textarea
                value={whatWorked}
                onChange={e => setWhatWorked(e.target.value)}
                placeholder="What went well? What strategies were effective..."
                className="rounded-xl mt-1"
                rows={2}
                data-testid="practice-video-worked"
              />
            </div>

            {/* What didn't work */}
            <div>
              <label className="text-sm font-semibold text-slate-600 flex items-center gap-1">
                <ThumbsDown className="w-3.5 h-3.5 text-red-400" /> What didn't work?
              </label>
              <Textarea
                value={whatDidntWork}
                onChange={e => setWhatDidntWork(e.target.value)}
                placeholder="What challenges did you face? What didn't go as planned..."
                className="rounded-xl mt-1"
                rows={2}
                data-testid="practice-video-didnt-work"
              />
            </div>

            <Button
              data-testid="submit-practice-video"
              onClick={handleUpload}
              disabled={uploading}
              className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold h-11 gap-2"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload Practice Video'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-auto">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-[#22D3EE]" /> Practice Video
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {/* Video Player */}
                <div className="rounded-xl overflow-hidden bg-black">
                  <video
                    src={`${BACKEND_URL}/api/uploads/${showDetail.filename}`}
                    controls
                    className="w-full max-h-[300px]"
                    data-testid="practice-video-player"
                  />
                </div>

                {/* Skill Labels */}
                <div className="flex flex-wrap gap-2">
                  {showDetail.skill_labels?.map((label, i) => (
                    <Badge key={`pvb-${i}`} className="rounded-full bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]/30 gap-1">
                      <Tag className="w-3 h-3" /> {label}
                    </Badge>
                  ))}
                </div>

                {/* Description Fields */}
                {showDetail.description && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">What was done</p>
                    <p className="text-sm text-slate-700">{showDetail.description}</p>
                  </div>
                )}

                {showDetail.how_completed && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">How the child completed the skill</p>
                    <p className="text-sm text-slate-700">{showDetail.how_completed}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {showDetail.what_worked && (
                    <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                      <p className="text-xs font-bold text-green-600 uppercase mb-1 flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" /> What worked
                      </p>
                      <p className="text-sm text-slate-700">{showDetail.what_worked}</p>
                    </div>
                  )}
                  {showDetail.what_didnt_work && (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                      <p className="text-xs font-bold text-red-500 uppercase mb-1 flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3" /> What didn't work
                      </p>
                      <p className="text-sm text-slate-700">{showDetail.what_didnt_work}</p>
                    </div>
                  )}
                </div>

                {/* Instructor Notes Section */}
                {isInstructor && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-bold text-slate-600 flex items-center gap-1 mb-2">
                      <MessageCircle className="w-4 h-4 text-purple-500" /> Instructor Feedback
                    </label>
                    <Textarea
                      value={instructorNote}
                      onChange={e => setInstructorNote(e.target.value)}
                      placeholder="Add notes or feedback for the parent about this practice..."
                      className="rounded-xl"
                      rows={3}
                      data-testid="instructor-note-input"
                    />
                    <Button
                      onClick={handleSaveInstructorNote}
                      disabled={savingNote}
                      className="mt-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold gap-2"
                      data-testid="save-instructor-note-btn"
                    >
                      {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                      Save Feedback
                    </Button>
                  </div>
                )}

                {/* Show instructor notes to parent */}
                {isParent && showDetail.instructor_notes && (
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                    <p className="text-xs font-bold text-purple-600 uppercase mb-1 flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" /> Instructor Feedback
                    </p>
                    <p className="text-sm text-slate-700">{showDetail.instructor_notes}</p>
                  </div>
                )}

                {/* Meta & Actions */}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-slate-400">
                    Uploaded by {showDetail.parent_name} on {new Date(showDetail.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  {(isParent || user?.is_admin) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full gap-1"
                      onClick={() => handleDelete(showDetail.video_id)}
                      data-testid="delete-practice-video-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
